import { useEffect, useState, type ReactNode } from 'react';

import { DATEIARTEN, rueckstandsStufe, type Dateiart } from '../../domain/rueckstand';
import { useT } from '../../i18n';
import { useAppStore } from '../../store/useAppStore';
import { Icon, type IconName } from '../../ui/Icon';
import { meldeRueckstand } from '../header/rueckstandMelden';
import styles from './Sheets.module.css';

/** Solange der Dialog offen ist, so oft neu zählen — ein Durchgang kann jederzeit enden. */
const NEU_ZAEHLEN_MS = 3000;

const ICONS: Record<'pakete' | Dateiart, IconName> = {
  pakete: 'list',
  screenshot: 'sheet',
  photo: 'camera',
  video: 'video',
  audio: 'mic',
  handwriting: 'scribble',
  annotation: 'markArrow',
  sonstige: 'doc',
};

/**
 * Was noch nicht übertragen ist, aufgeschlüsselt nach Art — ganz unten im Einstellungsdialog
 * (Vorgabe des Nutzers, 2026-10-02).
 *
 * Die Pille in der Kopfzeile zeigt nur Pakete und Dateien; hier steht, **welche** Dateien. Wer
 * vor dem Einsammeln der Geräte nachsieht, will wissen, ob noch ein Video unterwegs ist oder
 * nur ein Bildschirmfoto.
 *
 * Gezählt wird nicht hier, sondern wie für die Pille (`server/rueckstand.ts`); die Zahlen
 * stehen im Store. Kacheln mit null bleiben stehen (grau): „0 Videos“ ist auch eine Auskunft.
 *
 * Kacheln statt Tabelle (Vorgabe des Nutzers, 2026-10-14): grau bei 0, gelb bis 5, rot darüber.
 * ⚠️ **Antippen statt Tooltip** — das iPad kennt kein Überfahren; der Name steht dann unter der
 * Reihe (wie `MitErklaerung`). `title` gilt nur am Desktop.
 */
export function RueckstandGruppe({
  Group,
}: {
  Group: (p: { label: string; children: ReactNode }) => ReactNode;
}) {
  const t = useT();
  const zahlen = useAppStore((s) => s.rueckstand);
  const autoUpload = useAppStore((s) => s.autoUpload);
  const [offen, setOffen] = useState<string | null>(null);

  useEffect(() => {
    void meldeRueckstand();
    const id = window.setInterval(() => void meldeRueckstand(), NEU_ZAEHLEN_MS);
    return () => window.clearInterval(id);
  }, []);

  const kacheln: { key: string; icon: IconName; name: string; zahl: number; stufe: string }[] = [
    { key: 'pakete', name: t.backlog.kinds.pakete, zahl: zahlen.pakete },
    ...DATEIARTEN.map((art) => ({ key: art, name: t.backlog.kinds[art], zahl: zahlen.jeArt[art] })),
  ].map((k) => ({ ...k, icon: ICONS[k.key as keyof typeof ICONS], stufe: rueckstandsStufe(k.zahl) }));
  if (zahlen.abgewiesen > 0) {
    kacheln.push({ key: 'abgewiesen', icon: 'warning', name: t.backlog.rejectedRow, zahl: zahlen.abgewiesen, stufe: 'viel' });
  }
  const gewaehlt = kacheln.find((k) => k.key === offen);

  return Group({
    label: t.backlog.title,
    children: (
      <>
        <div className={styles.rueckstand}>
          {kacheln.map((k) => (
            <button
              key={k.key}
              type="button"
              className={styles.rueckstandKachel}
              data-stufe={k.stufe}
              aria-pressed={offen === k.key}
              aria-label={k.name + ': ' + k.zahl}
              title={k.name}
              onClick={() => setOffen(offen === k.key ? null : k.key)}
            >
              <Icon name={k.icon} size={20} />
              <b>{k.zahl}</b>
            </button>
          ))}
        </div>
        {gewaehlt && <div className={styles.textNote}>{gewaehlt.name + ': ' + gewaehlt.zahl}</div>}
        {!autoUpload && <div className={styles.textNote}>{t.backlog.autoOff}</div>}
      </>
    ),
  });
}
